import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  useMemo,
  useState,
} from "react";


function transitionLabel(item) {
  if (!item) {
    return "";
  }

  const league =
    item?.from?.league_name ||
    "unbekannter Liga";

  if (
    item.transition_type ===
    "promoted"
  ) {
    return (
      `Neu in dieser Liga · ` +
      `aufgestiegen aus ${league}`
    );
  }

  if (
    item.transition_type ===
    "relegated"
  ) {
    return (
      `Neu in dieser Liga · ` +
      `abgestiegen aus ${league}`
    );
  }

  return (
    `Neu in dieser Liga · ` +
    `Herkunft: ${league}`
  );
}


function transitionColor(
  transitionType
) {
  if (
    transitionType === "promoted"
  ) {
    return "success.main";
  }

  if (
    transitionType === "relegated"
  ) {
    return "warning.main";
  }

  return "grey.500";
}


const RULE_META = {
  promotion: {
    label: "Direktaufstieg",
    color: "success.main",
  },

  promotion_playoff: {
    label: "Aufstiegs-Playoff",
    color: "info.main",
  },

  championship_round: {
    label: "Meisterrunde",
    color: "primary.main",
  },

  relegation_round: {
    label: "Abstiegsrunde",
    color: "secondary.main",
  },

  relegation_playoff: {
    label: "Relegations-Playoff",
    color: "warning.main",
  },

  relegation: {
    label: "Abstieg",
    color: "error.main",
  },

  other: {
    label: "Weitere Platzregel",
    color: "grey.500",
  },
};


function getRuleMeta(
  category
) {
  return (
    RULE_META[category] ||
    null
  );
}


const PROVIDER_RULE_LABELS = {
  UEFA_CHAMPIONS_LEAGUE:
    "Champions League",

  UEFA_CHAMPIONS_LEAGUE_QUALIFIERS:
    "Champions-League-Qualifikation",

  UEFA_EUROPA_LEAGUE:
    "Europa League",

  UEFA_EUROPA_LEAGUE_QUALIFIERS:
    "Europa-League-Qualifikation",

  UEFA_CONFERENCE_LEAGUE:
    "Conference League",

  UEFA_CONFERENCE_LEAGUE_QUALIFIERS:
    "Conference-League-Qualifikation",

  UEFA_CONFERENCE_LEAGUE_PLAY_OFFS:
    "Conference-League-Play-offs",

  CHAMPIONSHIP_ROUND:
    "Meisterrunde",

  RELEGATION_ROUND:
    "Abstiegsrunde",

  RELEGATION:
    "Abstieg",

  PROMOTION:
    "Aufstieg",

  PROMOTION_PLAY_OFFS:
    "Aufstiegs-Play-offs",

  RELEGATION_PLAY_OFFS:
    "Relegations-Play-offs",
};


function providerRuleLabel(rule) {
  if (!rule) {
    return "";
  }

  const providerType =
    rule?.provider_rule?.type ||
    {};

  const developerName =
    providerType
      ?.developer_name ||
    "";

  if (
    developerName &&
    PROVIDER_RULE_LABELS[
      developerName
    ]
  ) {
    return (
      PROVIDER_RULE_LABELS[
        developerName
      ]
    );
  }

  /*
   * Für Sportmonks-Typen, die wir heute
   * noch nicht kennen, behalten wir den
   * Provider-Namen vollständig sichtbar.
   */
  if (providerType?.name) {
    return String(
      providerType.name
    );
  }

  if (developerName) {
    return String(
      developerName
    )
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) =>
        char.toUpperCase()
      );
  }

  return "";
}


function ruleDisplayLabel(rule) {
  const providerLabel =
    providerRuleLabel(
      rule
    );

  /*
   * Die konkrete Sportmonks-Regel ist
   * informativer als unsere abstrakte
   * Sammelkategorie "other".
   */
  if (providerLabel) {
    return providerLabel;
  }

  const meta =
    getRuleMeta(
      rule?.rule_category
    );

  return (
    meta?.label ||
    "Platzregel"
  );
}


function ruleTooltip(rules) {
  const items =
    Array.isArray(rules)
      ? rules
      : rules
      ? [rules]
      : [];

  if (items.length === 0) {
    return "";
  }

  const lines = [];
  const seen = new Set();

  for (const rule of items) {
    const label =
      ruleDisplayLabel(
        rule
      );

    /*
     * rule_text enthält teilweise noch einmal
     * dieselbe Provider-Bezeichnung in technisch
     * aufbereiteter Form.
     *
     * Sobald wir eine konkrete provider_rule
     * besitzen, bevorzugen wir deshalb deren
     * verständliche Bezeichnung.
     */
    let line = label;

    if (
      !providerRuleLabel(rule) &&
      rule?.rule_text
    ) {
      line =
        `${label} · ${rule.rule_text}`;
    }

    const normalized =
      String(line)
        .trim()
        .toLowerCase();

    if (
      !normalized ||
      seen.has(normalized)
    ) {
      continue;
    }

    seen.add(normalized);
    lines.push(line);
  }

  return lines.join("\n");
}


function SmallDiamond({
  color,
  size = 9,
}) {
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        display: "inline-block",
        flexShrink: 0,
        bgcolor: color,
        borderRadius: "2px",
        transform: "rotate(45deg)",
        boxShadow:
          "0 0 0 1px rgba(255,255,255,0.85)",
      }}
    />
  );
}




function playoffOutcomeLabel(
  item
) {
  if (!item) {
    return "";
  }

  if (
    item.rule_category ===
    "promotion_playoff"
  ) {
    if (item.successful) {
      return (
        "Aufstiegs-Play-offs erfolgreich" +
        (
          item.target_league_name
            ? ` · danach ${item.target_league_name}`
            : ""
        )
      );
    }

    return (
      "In den Aufstiegs-Play-offs ausgeschieden"
    );
  }

  if (
    item.rule_category ===
    "relegation_playoff"
  ) {
    if (item.successful) {
      return (
        "Relegations-Play-offs erfolgreich · Liga gehalten"
      );
    }

    return (
      "Relegations-Play-offs verloren" +
      (
        item.target_league_name
          ? ` · danach ${item.target_league_name}`
          : ""
      )
    );
  }

  return "";
}


function PlayoffOutcomeMark({
  item,
}) {
  if (!item) {
    return null;
  }

  return (
    <Tooltip
      title={
        playoffOutcomeLabel(
          item
        )
      }
      arrow
    >
      <Box
        component="span"
        sx={{
          ml: 0.2,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: 14,
          height: 14,
          borderRadius: "50%",
          fontSize: "0.67rem",
          lineHeight: 1,
          fontWeight: 800,
          cursor: "help",
          color:
            item.successful
              ? "success.dark"
              : "text.secondary",
          border: "1px solid",
          borderColor:
            item.successful
              ? "success.light"
              : "grey.400",
          backgroundColor:
            item.successful
              ? "rgba(46,125,50,0.055)"
              : "rgba(0,0,0,0.025)",
        }}
      >
        {item.successful
          ? "✓"
          : "×"}
      </Box>
    </Tooltip>
  );
}


function LegendItem({
  color,
  label,
  shape = "bar",
}) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
      }}
    >
      {shape === "diamond" ? (
        <SmallDiamond
          color={color}
          size={8}
        />
      ) : (
        <Box
          sx={{
            width: 4,
            height: 16,
            borderRadius: 4,
            bgcolor: color,
          }}
        />
      )}

      <Typography
        variant="caption"
        color="text.secondary"
      >
        {label}
      </Typography>
    </Box>
  );
}


function formatFixed(value, digits = 3) {
  if (
    value === null ||
    value === undefined ||
    !Number.isFinite(Number(value))
  ) {
    return "–";
  }

  return Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatPercent(value, digits = 2) {
  if (
    value === null ||
    value === undefined ||
    !Number.isFinite(Number(value))
  ) {
    return "–";
  }

  return (
    Number(value * 100).toLocaleString("de-DE", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }) + " %"
  );
}

function tableValues(
  row,
  tableMode
) {
  const values =
    row?.[tableMode] || {};

  return {
    games_played:
      Number(values.games_played || 0),
    won:
      Number(values.won || 0),
    draw:
      Number(values.draw || 0),
    lost:
      Number(values.lost || 0),
    goals_scored:
      Number(values.goals_scored || 0),
    goals_against:
      Number(values.goals_against || 0),
    points:
      Number(values.points || 0),
  };
}


function displayTableValue(
  value,
  decimalValues,
  digits = 2
) {
  if (!decimalValues) {
    return value;
  }

  return formatFixed(
    value,
    digits
  );
}


function defaultSort(
  sourceRows,
  tableMode
) {
  return [...sourceRows].sort((teamOne, teamTwo) => {
    const one =
      tableValues(
        teamOne,
        tableMode
      );

    const two =
      tableValues(
        teamTwo,
        tableMode
      );

    const pointsDiff =
      Number(two.points || 0) -
      Number(one.points || 0);

    if (pointsDiff !== 0) {
      return pointsDiff;
    }

    const goalDiffOne =
      Number(one.goals_scored || 0) -
      Number(one.goals_against || 0);

    const goalDiffTwo =
      Number(two.goals_scored || 0) -
      Number(two.goals_against || 0);

    if (goalDiffOne !== goalDiffTwo) {
      return goalDiffTwo - goalDiffOne;
    }

    const goalsDiff =
      Number(two.goals_scored || 0) -
      Number(one.goals_scored || 0);

    if (goalsDiff !== 0) {
      return goalsDiff;
    }

    return String(
      teamOne.team_name || ""
    ).localeCompare(
      String(
        teamTwo.team_name || ""
      )
    );
  });
}

function calculateSummary(
  rows
) {
  let games = 0;
  let homeWins = 0;
  let draws = 0;
  let awayWins = 0;
  let homeGoals = 0;
  let awayGoals = 0;

  for (const row of rows) {
    const home =
      tableValues(
        row,
        "home"
      );

    games += Number(
      home.games_played || 0
    );

    homeWins += Number(
      home.won || 0
    );

    draws += Number(
      home.draw || 0
    );

    awayWins += Number(
      home.lost || 0
    );

    homeGoals += Number(
      home.goals_scored || 0
    );

    awayGoals += Number(
      home.goals_against || 0
    );
  }

  const goals = homeGoals + awayGoals;

  return {
    fixtures_played: games,
    home_wins: homeWins,
    draws,
    away_wins: awayWins,
    home_goals: homeGoals,
    away_goals: awayGoals,
    goals,
    goal_average:
      games > 0
        ? goals / games
        : null,
    home_share:
      goals > 0
        ? homeGoals / goals
        : null,
    home_factor:
      goals > 0
        ? homeGoals / (goals / 2)
        : null,
  };
}

function DefaultStatsPanel({
  summary,
  decimalValues = false,
}) {
  const [goalMode, setGoalMode] = useState("average");
  const [resultMode, setResultMode] = useState("count");

  const games = Number(summary?.fixtures_played || 0);
  const homeWins = Number(summary?.home_wins || 0);
  const draws = Number(summary?.draws || 0);
  const awayWins = Number(summary?.away_wins || 0);
  const homeGoals = Number(summary?.home_goals || 0);
  const awayGoals = Number(summary?.away_goals || 0);

  const totalGoals = Number(
    summary?.goals ??
    (homeGoals + awayGoals)
  );

  const goalAverage =
    Number.isFinite(Number(summary?.goal_average))
      ? Number(summary.goal_average)
      : games > 0
      ? totalGoals / games
      : null;

  const homeShare =
    Number.isFinite(Number(summary?.home_share))
      ? Number(summary.home_share)
      : totalGoals > 0
      ? homeGoals / totalGoals
      : null;

  const homeFactor =
    Number.isFinite(Number(summary?.home_factor))
      ? Number(summary.home_factor)
      : totalGoals > 0
      ? homeGoals / (totalGoals / 2)
      : null;

  function goalValue() {
    switch (goalMode) {
      case "homeAway":
        return decimalValues
          ? `${formatFixed(homeGoals, 2)}-${formatFixed(awayGoals, 2)}`
          : `${homeGoals}-${awayGoals}`;

      case "total":
        return decimalValues
          ? formatFixed(totalGoals, 2)
          : String(totalGoals);

      case "homeShare":
        return formatPercent(homeShare, 1);

      case "homeFactor":
        return formatFixed(homeFactor, 3);

      case "average":
      default:
        return formatFixed(goalAverage, 3);
    }
  }

  function oneXTwoValue(count) {
    if (resultMode === "percent") {
      return games > 0
        ? formatPercent(count / games, 2)
        : "–";
    }

    if (resultMode === "odds") {
      return count > 0
        ? formatFixed(games / count, 2)
        : "–";
    }

    return decimalValues
      ? formatFixed(count, 2)
      : String(count);
  }

  const selectorSx = {
    "& .MuiToggleButton-root": {
      px: 1.15,
      py: 0.5,
      fontSize: "0.72rem",
      textTransform: "none",
      fontWeight: 600,
    },
    "& .Mui-selected": {
      fontWeight: 800,
    },
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "1fr 1fr",
        },
        gap: 1.5,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          backgroundColor: "rgba(255,255,255,0.72)",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography
            sx={{
              fontWeight: 800,
              minWidth: 45,
            }}
          >
            TORE
          </Typography>

          <ToggleButtonGroup
            value={goalMode}
            exclusive
            size="small"
            onChange={(event, value) => {
              if (value) {
                setGoalMode(value);
              }
            }}
            sx={selectorSx}
          >
            <ToggleButton value="average">
              Ø
            </ToggleButton>

            <ToggleButton value="homeAway">
              H/A
            </ToggleButton>

            <ToggleButton value="total">
              Gesamt
            </ToggleButton>

            <ToggleButton value="homeShare">
              Heimanteil
            </ToggleButton>

            <ToggleButton value="homeFactor">
              Heimfaktor
            </ToggleButton>
          </ToggleButtonGroup>

          <Typography
            sx={{
              ml: "auto",
              fontSize: "1.15rem",
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {goalValue()}
          </Typography>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          backgroundColor: "rgba(255,255,255,0.72)",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography
            sx={{
              fontWeight: 800,
              minWidth: 45,
            }}
          >
            1X2
          </Typography>

          <ToggleButtonGroup
            value={resultMode}
            exclusive
            size="small"
            onChange={(event, value) => {
              if (value) {
                setResultMode(value);
              }
            }}
            sx={selectorSx}
          >
            <ToggleButton value="count">
              Anzahl
            </ToggleButton>

            <ToggleButton value="percent">
              %
            </ToggleButton>

            <ToggleButton value="odds">
              Quote
            </ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{
              ml: "auto",
              display: "grid",
              gridTemplateColumns:
                "repeat(3, minmax(65px, auto))",
              gap: 1.5,
            }}
          >
            {[homeWins, draws, awayWins].map((value, index) => (
              <Typography
                key={index}
                sx={{
                  textAlign: "center",
                  fontSize: "1.05rem",
                  fontWeight: 800,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {oneXTwoValue(value)}
              </Typography>
            ))}
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

export function LeagueTablePanel({
  rows = [],
  summary = null,
  decimalValues = false,
  title = "",
  subtitle = "",
  showTableMode = true,
  initialTableMode = "overall",
  renderStats = true,
  statsContent = null,
  transitionItems = [],
  standingRules = [],
  outgoingItems = [],
  playoffOutcomes = [],
}) {
  const [tableMode, setTableMode] = useState(
    initialTableMode
  );

  const sortedRows = useMemo(
    () =>
      defaultSort(
        rows,
        tableMode
      ),
    [
      rows,
      tableMode,
    ]
  );

  const calculatedSummary = useMemo(
    () =>
      summary ||
      calculateSummary(
        rows
      ),
    [
      summary,
      rows,
    ]
  );

  const transitionByTeam =
    useMemo(() => {
      const map = new Map();

      for (
        const item of
        transitionItems || []
      ) {
        const teamId =
          Number(item?.team_id);

        if (
          Number.isFinite(teamId)
        ) {
          map.set(
            teamId,
            item
          );
        }
      }

      return map;
    }, [transitionItems]);


  const playoffOutcomeByTeam =
    useMemo(() => {
      const map = new Map();

      for (
        const item of
        playoffOutcomes || []
      ) {
        const teamId =
          Number(
            item?.team_id
          );

        if (
          Number.isFinite(teamId)
        ) {
          map.set(
            teamId,
            item
          );
        }
      }

      return map;
    }, [playoffOutcomes]);


  const standingRulesByTeam =
    useMemo(() => {
      const map = new Map();

      for (
        const item of
        standingRules || []
      ) {
        const teamId =
          Number(item?.team_id);

        if (
          !Number.isFinite(teamId)
        ) {
          continue;
        }

        const existing =
          map.get(teamId) || [];

        existing.push(item);

        map.set(
          teamId,
          existing
        );
      }

      return map;
    }, [standingRules]);


  const transitionLegend =
    useMemo(() => {
      const types =
        new Set(
          (transitionItems || [])
            .map(
              (item) =>
                item?.transition_type
            )
            .filter(Boolean)
        );

      const result = [];

      if (
        types.has("promoted")
      ) {
        result.push({
          key: "promoted",
          label:
            "Neu aus tieferer Liga",
          color:
            transitionColor(
              "promoted"
            ),
        });
      }

      if (
        types.has("relegated")
      ) {
        result.push({
          key: "relegated",
          label:
            "Neu aus höherer Liga",
          color:
            transitionColor(
              "relegated"
            ),
        });
      }

      return result;
    }, [transitionItems]);


  const standingRuleLegend =
    useMemo(() => {
      const categories =
        new Set(
          (standingRules || [])
            .map(
              (item) =>
                item?.rule_category
            )
            .filter(Boolean)
        );

      return [
        "promotion",
        "promotion_playoff",
        "championship_round",
        "relegation_round",
        "relegation_playoff",
        "relegation",
        "other",
      ]
        .filter(
          (category) =>
            categories.has(
              category
            )
        )
        .map(
          (category) => ({
            key: category,
            ...getRuleMeta(
              category
            ),
          })
        )
        .filter(
          (item) =>
            item.label &&
            item.color
        );
    }, [standingRules]);


  const selectorSx = {
    "& .MuiToggleButton-root": {
      px: 1.15,
      py: 0.5,
      fontSize: "0.72rem",
      textTransform: "none",
      fontWeight: 600,
    },
    "& .Mui-selected": {
      fontWeight: 800,
    },
  };

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        background:
          "linear-gradient(145deg, rgba(226,241,248,0.92) 0%, rgba(244,250,252,0.96) 48%, rgba(232,245,250,0.92) 100%)",
        border:
          "1px solid rgba(80,120,140,0.18)",
      }}
    >
      {(title || subtitle || showTableMode) && (
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Box>
            {title && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                {title}
              </Typography>
            )}

            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {showTableMode && (
            <ToggleButtonGroup
              value={tableMode}
              exclusive
              size="small"
              onChange={(event, value) => {
                if (value) {
                  setTableMode(value);
                }
              }}
              sx={selectorSx}
            >
              <ToggleButton value="overall">
                Total
              </ToggleButton>

              <ToggleButton value="home">
                Home
              </ToggleButton>

              <ToggleButton value="away">
                Away
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      )}

      <Paper
        variant="outlined"
        sx={{
          overflowX: "auto",
          borderRadius: 2,
          backgroundColor: "rgba(255,255,255,0.94)",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 720,
            "& th": {
              fontWeight: 700,
              backgroundColor:
                "rgba(235,244,248,0.9)",
            },
            "& tbody tr:nth-of-type(even)": {
              backgroundColor:
                "rgba(239,247,250,0.5)",
            },
            "& tbody tr:hover": {
              backgroundColor:
                "rgba(218,238,247,0.65)",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{ width: 55 }}
              >
                #
              </TableCell>

              <TableCell>
                Team
              </TableCell>

              <TableCell align="center">
                Sp
              </TableCell>

              <TableCell align="center">
                S
              </TableCell>

              <TableCell align="center">
                U
              </TableCell>

              <TableCell align="center">
                N
              </TableCell>

              <TableCell align="center">
                Tore
              </TableCell>

              <TableCell align="center">
                Diff.
              </TableCell>

              <TableCell align="center">
                Pkt
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.map((row, index) => {
              const values =
                tableValues(
                  row,
                  tableMode
                );

              const diff =
                Number(
                  values.goals_scored ||
                  0
                ) -
                Number(
                  values.goals_against ||
                  0
                );

              const teamId =
                Number(
                  row.team_id ??
                  row.id
                );

              const transition =
                tableMode === "overall"
                  ? transitionByTeam.get(
                      teamId
                    )
                  : null;

              const playoffOutcome =
                tableMode === "overall"
                  ? playoffOutcomeByTeam.get(
                      teamId
                    )
                  : null;

              const teamStandingRules =
                tableMode === "overall"
                  ? (
                      standingRulesByTeam.get(
                        teamId
                      ) || []
                    )
                  : [];

              const primaryStandingRule =
                teamStandingRules.find(
                  (rule) =>
                    rule?.is_regular_stage &&
                    getRuleMeta(
                      rule?.rule_category
                    )
                ) ||
                teamStandingRules.find(
                  (rule) =>
                    getRuleMeta(
                      rule?.rule_category
                    )
                ) ||
                teamStandingRules[0] ||
                null;

              const ruleMeta =
                primaryStandingRule
                  ? (
                      getRuleMeta(
                        primaryStandingRule
                          .rule_category
                      ) ||
                      RULE_META.other
                    )
                  : null;

              return (
                <TableRow
                  key={
                    row.team_id ??
                    row.id ??
                    index
                  }
                >
                  <TableCell
                    align="center"
                    sx={{
                      position: "relative",
                      fontWeight: 600,
                    }}
                  >
                    {ruleMeta && (
                      <Tooltip
                        title={
                          <Box
                            component="span"
                            sx={{
                              whiteSpace:
                                "pre-line",
                            }}
                          >
                            {ruleTooltip(
                              teamStandingRules
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Box
                          component="span"
                          sx={{
                            position:
                              "absolute",
                            left: 4,
                            top: 5,
                            bottom: 5,
                            width: 4,
                            borderRadius: 4,
                            bgcolor:
                              ruleMeta.color,
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}

                    {index + 1}
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      {row.logo_path && (
                        <img
                          src={row.logo_path}
                          alt=""
                          style={{
                            width: 24,
                            height: 24,
                            objectFit: "contain",
                          }}
                        />
                      )}

                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {row.team_name}
                      </Typography>

                      {(transition || playoffOutcome) && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            ml: 0.5,
                            flexShrink: 0,
                          }}
                        >
                          {transition && (
                            <Tooltip
                              title={
                                transitionLabel(
                                  transition
                                )
                              }
                              arrow
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 14,
                                  height: 14,
                                  cursor: "help",
                                  flexShrink: 0,
                                }}
                              >
                                <SmallDiamond
                                  color={
                                    transitionColor(
                                      transition
                                        .transition_type
                                    )
                                  }
                                />
                              </Box>
                            </Tooltip>
                          )}

                          {playoffOutcome && (
                            <PlayoffOutcomeMark
                              item={
                                playoffOutcome
                              }
                            />
                          )}
                        </Box>
                      )}
                    </Box>


                  </TableCell>

                  <TableCell align="center">
                    {displayTableValue(
                      values.games_played,
                      false,
                      0
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {displayTableValue(
                      values.won,
                      decimalValues
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {displayTableValue(
                      values.draw,
                      decimalValues
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {displayTableValue(
                      values.lost,
                      decimalValues
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {displayTableValue(
                      values.goals_scored,
                      decimalValues
                    )}
                    {"-"}
                    {displayTableValue(
                      values.goals_against,
                      decimalValues
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {diff > 0 ? "+" : ""}
                    {displayTableValue(
                      diff,
                      decimalValues
                    )}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    {displayTableValue(
                      values.points,
                      decimalValues
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {tableMode === "overall" &&
        (
          transitionLegend.length > 0 ||
          standingRuleLegend.length > 0
        ) && (
          <Box
            sx={{
              mt: 1.15,
              px: 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.75,
            }}
          >
            {transitionLegend.map(
              (item) => (
                <LegendItem
                  key={
                    `transition-${item.key}`
                  }
                  color={item.color}
                  label={item.label}
                  shape="diamond"
                />
              )
            )}

            {transitionLegend.length > 0 &&
              standingRuleLegend.length > 0 && (
                <Box
                  sx={{
                    width: "1px",
                    height: 18,
                    bgcolor: "divider",
                  }}
                />
              )}

            {standingRuleLegend.map(
              (item) => (
                <LegendItem
                  key={
                    `rule-${item.key}`
                  }
                  color={item.color}
                  label={item.label}
                />
              )
            )}
          </Box>
        )}

      {renderStats &&
        (statsContent || (
          <DefaultStatsPanel
            summary={calculatedSummary}
            decimalValues={decimalValues}
          />
        ))}
    </Box>
  );
}
